import { useNavigate } from "@solidjs/router";
import { type Component, createSignal, Match, Switch } from "solid-js";
import { twJoin } from "tailwind-merge";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { PricingPageContext } from "~/solidJs/pages/PricingPage/methods";
import { getPrivateMessageFromError, isUserFree, useCustomContext } from "~/solidJs/shared/helpers";
import { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types/IUser";
import { Button, type ButtonMainProps } from "~/solidJs/shared/ui";
import { showConfirmPlanChangeModal } from "~/solidJs/widgets/ConfirmPlanChangeModal";
import { showCreateTeamModal } from "~/solidJs/widgets/CreateTeamModalWidget/methods";
import { TariffBtnModeEnum } from "../helpers/tariffLookUpModes";
import { checkoutClickHandler } from "./shared/handlers";

type CheckoutHookParamsV2 = {
	nameId: () => UserSubscriptionPlanEnum;
	btnMode: () => TariffBtnModeEnum | undefined;
	title: () => string;
	sourceGlobalId: () => string | undefined;
	isTrial: () => boolean;
	isFree: () => boolean;
};
/**
 *
 * @deprecated the old checkout function
 * @returns
 */
const useCheckoutHandlerV2 = (params: CheckoutHookParamsV2) => {
	const navigate = useNavigate();
	const premiumPopUpStore = useCustomContext(PricingPageContext);
	const [isPending, setIsPending] = createSignal(false);

	const handleCheckout = async () => {
		const user = getUserStore();
		const nameId = params.nameId();
		const btnMode = params.btnMode();
		const title = params.title();
		const _sourceGlobalId = params.sourceGlobalId();
		const isTrial = params.isTrial();
		const isFree = params.isFree();

		try {
			setIsPending(true);

			if (isFree) {
				return;
			}

			if (btnMode === TariffBtnModeEnum.Current) {
				return;
			}

			if (!user) {
				navigate("/auth/sign-in");
				return;
			}

			if (isUserFree(user)) {
				await checkoutClickHandler({
					nameId,
					activeInterval: premiumPopUpStore?.getSubscriptionLength() || "12mo",
					isTrial,
					type: premiumPopUpStore?.getIsRuCards() ? "yookassa" : "stripe",
					promoCode: premiumPopUpStore?.usePromocode.getPromoCode()?.promoCode
				});
				return;
			}

			const isDowngrade = btnMode === TariffBtnModeEnum.Downgrade;

			if (isDowngrade) {
				showConfirmPlanChangeModal(nameId, "downgrade");
				return;
			}

			const isUpgrade = btnMode === TariffBtnModeEnum.Upgrade;

			if (isUpgrade) {
				showConfirmPlanChangeModal(nameId, "upgrade");
				return;
			}

			// Default case - regular checkout
			await checkoutClickHandler({
				nameId,
				activeInterval: premiumPopUpStore?.getSubscriptionLength() || "12mo",
				isTrial,
				type: premiumPopUpStore?.getIsRuCards() ? "yookassa" : "stripe",
				promoCode: premiumPopUpStore?.usePromocode.getPromoCode()?.promoCode
			});
		} catch (error) {
			EventAnalytics.sendErrorInfo("error-checkout-handler-v2", {
				message: getPrivateMessageFromError(error),
				nameId,
				title
			});
		} finally {
			setIsPending(false);
		}
	};

	return { handleCheckout, isPending };
};

type CheckoutButtonFeatureProps = {
	nameId: UserSubscriptionPlanEnum;
	name: string;
	btnMode: TariffBtnModeEnum | undefined;
	isSelected: boolean;
	isTrial: boolean;
	getBtnText?: (isCurrentPlan: boolean, btnMode: TariffBtnModeEnum | undefined) => string;
	clickHandler?: (e: MouseEvent) => void;
	btnProps?: ButtonMainProps<"button">;
};

export const CheckoutButtonFeatureV2: Component<CheckoutButtonFeatureProps> = (props) => {
	const { handleCheckout, isPending } = useCheckoutHandlerV2({
		nameId: () => props.nameId,
		btnMode: () => props.btnMode,
		title: () => props.name,
		sourceGlobalId: () => undefined, // Not available in CheckoutButtonFeatureProps
		isTrial: () => props.isTrial,
		isFree: () => props.nameId === UserSubscriptionPlanEnum.Free
	});
	const isCurrentPlan = () => getUserStore()?.group === props.nameId;
	return (
		<Button
			size={"large"}
			class={twJoin("w-full leading-4")}
			variant={props.isSelected ? "SecondaryNeutral" : "TertiaryAccent"}
			disabled={isCurrentPlan() || props.btnMode === TariffBtnModeEnum.Current}
			pending={isPending()}
			data-test={"premium-submit"}
			data-test-tariff={`premium-submit-${props.nameId}`}
			{...(props.nameId === "teams"
				? {
						onClick() {
							showCreateTeamModal();
						}
					}
				: {
						onClick: (e) => {
							handleCheckout();
							props.clickHandler?.(e);
						}
					})}
			{...(props.btnProps ? props.btnProps : {})}
		>
			<Switch>
				<Match when={props.getBtnText} keyed>
					{(fc) =>
						fc(
							isCurrentPlan() || props.btnMode === TariffBtnModeEnum.Current,
							props.btnMode
						)
					}
				</Match>
				<Match when={isCurrentPlan() || props.btnMode === TariffBtnModeEnum.Current}>
					Current plan
				</Match>
				<Match when={props.isTrial}>Get Free Trial</Match>
				<Match when={props.btnMode === "downgrade"}>Downgrade</Match>
				<Match when={props.btnMode === "upgrade"}>Upgrade to {props.name}</Match>
				<Match when={props.btnMode === undefined}>Upgrade to {props.name}</Match>
			</Switch>
		</Button>
	);
};
